import { createSelector } from 'reselect';

import type { State } from 'reducers';
import type { DeedState } from 'entities/deed/reducer';

export const selectDeedState = (state: State): DeedState => state.get('entities').deed;

const now = new Date();

export const selectDeeds = createSelector(selectDeedState, (deedState) => deedState.get('deeds'));

export const selectDeedsByType = createSelector(
  selectDeeds,
  (_deeds, deedType) => deedType,
  (deeds, deedType) => deeds.filter((deed) => Boolean(deed?.type === deedType))
);

export const selectUpcomingDeeds = createSelector(selectDeedsByType, (deeds) =>
  deeds.filter((deed) => !!deed && deed.endingAt > now).sort((a, b) => a.startingAt - b.startingAt)
);

export const selectDeedById = createSelector(
  selectDeeds,
  (_deeds, deedId) => deedId,
  (deeds, deedId) => deeds.get(deedId)
);

export const selectUpcomingDeedsForUser = createSelector(
  selectDeeds,
  (_deeds, user) => user,
  (deeds, user) =>
    deeds
      .filter((deed) =>
        Boolean(
          deed.endingAt > now && (deed?.isUserAttending(user) || deed?.isUserWaitlisted(user))
        )
      )
      .sort((a, b) => a.startingAt - b.startingAt)
);

export const selectPastDeedsForUser = createSelector(
  selectDeeds,
  (_deeds, user) => user,
  (deeds, user) =>
    deeds
      .filter(
        (deed) =>
          !!deed &&
          deed.endingAt < now &&
          (deed.isUserAttending(user) || deed.isUserWaitlisted(user))
      )
      .sort((a, b) => b.startingAt - a.startingAt)
);

export const selectDeedsCreatedByEmployeesForApprove = createSelector(selectDeeds, (deeds) =>
  deeds
    .filter((deed) => !!deed && deed.submitter && deed.status === 'pending')
    .sort((a, b) => b.createdAt - a.createdAt)
);

export const selectUpcomingDeedsLoaded = createSelector(
  selectDeedState,
  (_state, userId) => userId,
  (deedState, userId) => deedState.get('upcomingDeedsLoaded').get(userId || 'me') || false
);

export const selectPastDeedsLoaded = createSelector(
  selectDeedState,
  (_state, userId) => userId,
  (deedState, userId) => deedState.get('pastDeedsLoaded').get(userId || 'me') || false
);
